<template>
  <div class="card mb-3">
    <div class="card-header">
      Notes
    </div>
    <div class="card-body">
      <!-- Notes List -->
      <div class="notes-list mb-3" v-if="notes.length > 0">
        <div v-for="note in notes" :key="note.id" class="note-item border-bottom pb-2 mb-2">
          <div class="d-flex justify-content-between">
            <strong>{{ note.user.name }}</strong>
            <small class="text-muted">{{ formatDate(note.created_at) }}</small>
          </div>
          <div class="note-content mt-1" v-html="renderContent(note.content)"></div>
        </div>
      </div>
      <div v-else class="text-muted mb-3">
        No notes yet.
      </div>

      <!-- Add Note Form -->
      <div v-if="canAddNote">
        <div class="form-group">
          <label for="note-textarea">Add Note</label>
          <textarea
            ref="noteTextarea"
            class="form-control"
            id="note-textarea"
            rows="3"
            v-model="noteContent"
            placeholder="Type @ to mention someone..."
          ></textarea>
        </div>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          @click="submitNote"
          :disabled="isSubmitting || !noteContent.trim()"
        >
          <i class="fa fa-paper-plane"></i>
          {{ isSubmitting ? 'Posting...' : 'Post Note' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Tribute from 'tributejs';

export default {
  props: {
    orderId: {
      type: Number,
      required: true
    },
    initialNotes: {
      type: Array,
      default: () => []
    },
    users: {
      type: Array,
      default: () => []
    },
    canAddNote: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      notes: this.initialNotes,
      noteContent: '',
      mentionedUserIds: [],
      isSubmitting: false,
      tribute: null
    };
  },

  mounted() {
    if (this.canAddNote) {
      this.$nextTick(() => {
        this.initTribute();
      });
    }
  },

  beforeDestroy() {
    if (this.tribute) {
      this.tribute.detach(this.$refs.noteTextarea);
    }
  },

  methods: {
    initTribute() {
      if (!this.$refs.noteTextarea) return;

      this.tribute = new Tribute({
        values: this.users.map(user => ({
          key: user.name,
          value: user.name,
          id: user.id
        })),
        selectTemplate: (item) => {
          if (!this.mentionedUserIds.includes(item.original.id)) {
            this.mentionedUserIds.push(item.original.id);
          }
          return `@[${item.original.value}](${item.original.id})`;
        },
        menuItemTemplate: (item) => {
          return item.original.key;
        },
        noMatchTemplate: () => '<span class="text-muted p-2">No users found</span>',
        lookup: 'key',
        fillAttr: 'value',
        allowSpaces: false,
        menuContainer: document.body,
        positionMenu: true
      });

      this.tribute.attach(this.$refs.noteTextarea);
    },

    async submitNote() {
      if (!this.noteContent.trim()) return;

      this.isSubmitting = true;

      try {
        const response = await axios.post('/notes', {
          content: { text: this.noteContent },
          noteable_type: 'App\\Models\\Order',
          noteable_id: this.orderId,
          mentioned_users: this.mentionedUserIds
        });

        if (response.data.success) {
          this.notes.push(response.data.note);
          this.noteContent = '';
          this.mentionedUserIds = [];
        }
      } catch (error) {
        console.error('Error posting note:', error);
        alert('Failed to post note. Please try again.');
      } finally {
        this.isSubmitting = false;
      }
    },

    renderContent(content) {
      if (!content || !content.text) return '';

      let text = content.text;

      // Convert @[Name](id) format to placeholder, then escape, then restore
      const mentions = [];
      text = text.replace(/@\[([^\]]+)\]\((\d+)\)/g, (match, name, id) => {
        const placeholder = `__MENTION_${mentions.length}__`;
        mentions.push(`<span class="mention badge badge-primary">@${this.escapeHtml(name)}</span>`);
        return placeholder;
      });

      // Escape HTML for the rest of the text
      text = this.escapeHtml(text);

      // Restore mentions
      mentions.forEach((mention, index) => {
        text = text.replace(`__MENTION_${index}__`, mention);
      });

      // Convert newlines to <br>
      text = text.replace(/\n/g, '<br>');

      return text;
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-MY', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.note-item:last-child {
  border-bottom: none !important;
  margin-bottom: 0 !important;
  padding-bottom: 0 !important;
}

.note-content {
  word-break: break-word;
}

.mention {
  font-size: 0.85em;
}
</style>
